import { nextTick, onBeforeUnmount, onMounted, type Ref, ref } from 'vue'

import EmblaCarousel, { type EmblaCarouselType, type EmblaOptionsType } from 'embla-carousel'

export interface UseEmblaCarouselOptions {
  /** Embla carousel options */
  options?: EmblaOptionsType
  /** Enable keyboard navigation with arrow keys */
  enableKeyboardNavigation?: boolean
  /** Callback fired when slide selection changes */
  onSelect?: (index: number) => void
}

export interface UseEmblaCarouselReturn {
  /** Ref to attach to the viewport element */
  emblaRef: Ref<HTMLElement | null>
  /** The Embla API instance */
  emblaApi: Ref<EmblaCarouselType | null>
  /** Currently selected slide index */
  selectedIndex: Ref<number>
  /** Navigate to previous slide */
  previousSlide: () => void
  /** Navigate to next slide */
  nextSlide: () => void
  /** Navigate to specific slide by index */
  scrollTo: (index: number) => void
  /** Initialize the carousel (call in mounted if needed manually) */
  initEmbla: () => void
}

export default function useEmblaCarousel(
  options: UseEmblaCarouselOptions = {}
): UseEmblaCarouselReturn {
  const {
    options: emblaOptions = {},
    enableKeyboardNavigation = false,
    onSelect: onSelectCallback
  } = options

  const emblaRef = ref<HTMLElement | null>(null)
  const emblaApi = ref<EmblaCarouselType | null>(null)
  const selectedIndex = ref(0)

  const onSelect = () => {
    if (!emblaApi.value) return
    selectedIndex.value = emblaApi.value.selectedScrollSnap()
    onSelectCallback?.(selectedIndex.value)
  }

  const initEmbla = () => {
    if (!emblaRef.value) return

    // Destroy existing instance before creating a new one to prevent memory leaks
    if (emblaApi.value) {
      emblaApi.value.destroy()
    }

    emblaApi.value = EmblaCarousel(emblaRef.value, {
      loop: true,
      align: 'center',
      ...emblaOptions
    })

    emblaApi.value.on('select', onSelect)
    onSelect()

    if (enableKeyboardNavigation) {
      // Make element focusable and add keyboard listener to the element itself
      emblaRef.value.setAttribute('tabindex', '0')
      // Remove existing listener before adding to prevent duplicates
      emblaRef.value.removeEventListener('keydown', handleKeydown)
      emblaRef.value.addEventListener('keydown', handleKeydown)
      emblaRef.value.focus()
    }
  }

  const previousSlide = () => {
    emblaApi.value?.scrollPrev()
  }

  const nextSlide = () => {
    emblaApi.value?.scrollNext()
  }

  const scrollTo = (index: number) => {
    emblaApi.value?.scrollTo(index)
  }

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      previousSlide()
    } else if (event.key === 'ArrowRight') {
      nextSlide()
    }
  }

  onMounted(() => {
    nextTick(() => {
      initEmbla()
    })
  })

  onBeforeUnmount(() => {
    if (enableKeyboardNavigation && emblaRef.value) {
      emblaRef.value.removeEventListener('keydown', handleKeydown)
    }
    if (emblaApi.value) {
      emblaApi.value.destroy()
    }
  })

  return {
    emblaRef,
    emblaApi,
    selectedIndex,
    previousSlide,
    nextSlide,
    scrollTo,
    initEmbla
  }
}
